// src/Views/Legal/Legal.js
// CLEAN VERSION - No banner, just tabs

import React, { useState, useEffect } from 'react';
import './Legal.css';
import { Icon } from '@iconify/react';
import { useNavigate, useLocation } from 'react-router-dom';

const Legal = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('terms'); // 'terms' | 'risk' | 'privacy'

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab && ['terms', 'risk', 'privacy'].includes(tab)) {
            setActiveTab(tab);
        }
    }, [location]);

    const renderContent = () => {
        switch (activeTab) {
            case 'risk': return <RiskPolicy />;
            case 'privacy': return <PrivacyPolicy />;
            default: return <TermsOfService />;
        }
    };

    return (
        <div className="legal-page">
            <div className="legal-header">
                <div className="legal-header__content">
                    <button className="legal-back-btn" onClick={() => navigate('/')}>
                        <Icon icon="solar:arrow-left-linear" /> Back to App
                    </button>
                    <h1>Legal Center</h1>
                    <p className="legal-subtitle">Transparency, Compliance, and User Protection.</p>
                </div>
            </div>

            <div className="legal-tabs-container">
                <button 
                    className={`legal-tab ${activeTab === 'terms' ? 'active' : ''}`}
                    onClick={() => setActiveTab('terms')}
                >
                    Terms of Service
                </button>
                <button 
                    className={`legal-tab ${activeTab === 'risk' ? 'active' : ''}`}
                    onClick={() => setActiveTab('risk')}
                >
                    Risk Disclosure
                </button>
                <button 
                    className={`legal-tab ${activeTab === 'privacy' ? 'active' : ''}`}
                    onClick={() => setActiveTab('privacy')}
                >
                    Privacy Policy
                </button>
            </div>

            <div className="legal-container">
                {renderContent()}
            </div>
        </div>
    );
};

/* --- 1. TERMS OF SERVICE (EXPANDED — v2.0 Feb 2026) --- */
const TermsOfService = () => (
    <div className="legal-text-fade">
        <p style={{ color: "#64748b", fontSize: "0.85rem", marginBottom: "1.5rem" }}>Last Updated: February 16, 2026</p>

        <section className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
                By creating an account, accessing, or using the AiPredict platform ("Service," "Platform," "we," "us," or "our"), you agree to be bound by these Terms of Service ("Terms"). These Terms constitute a legally binding agreement between you and AiPredict. If you do not agree to these Terms, you must immediately discontinue your use of the Service.
            </p>
            <p>
                We reserve the right to modify these Terms at any time. Continued use of the Platform following any changes signifies your acceptance of the new Terms.
            </p>
        </section>

        <section className="legal-section">
            <h2>2. Eligibility and Account Security</h2>
            <p>
                You must be at least 18 years of age (or the legal age of majority in your jurisdiction) to use this Service. By using AiPredict, you represent and warrant that you meet this requirement.
            </p>
            <p>
                You are responsible for maintaining the confidentiality of your login credentials. You agree to notify us immediately of any unauthorized use of your account. AiPredict is not liable for any loss or damage arising from your failure to protect your account information.
            </p>
        </section>

        <section className="legal-section">
            <h2>3. Nature of Services</h2>
            <p>
                AiPredict provides two categories of service:
            </p>
            <ul className="legal-list">
                <li><strong>Prediction & Analytics:</strong> AI-driven football predictions, match analysis, and crypto market insights delivered through our web and mobile platforms. These are informational tools only and do not constitute financial or betting advice.</li>
                <li><strong>Automated Trading Service ("Crypto AI Bot"):</strong> An optional, paid subscription service where our software executes cryptocurrency trades on your behalf through your personal exchange account. By enrolling, you authorize AiPredict to place, modify, and close trades using the API credentials you provide.</li>
            </ul>
            <p>
                <strong>THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS.</strong> We expressly disclaim all warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee that the Service will be uninterrupted, timely, secure, or error-free.
            </p>
        </section>

        <section className="legal-section">
            <h2>4. Automated Trading Service — Additional Terms</h2>
            <p>
                The following additional terms apply specifically to users who enroll in the Crypto AI Bot service. By submitting your API keys and subscribing, you acknowledge and agree to each of the following:
            </p>
            <ul className="legal-list">
                <li><strong>Authorization to Trade:</strong> You grant AiPredict limited, revocable authorization to place, manage, and close trades on your connected exchange account using the API credentials you supply. This authorization is limited strictly to trading functions and does not include withdrawal, transfer, or any other account management capability.</li>
                <li><strong>No Guaranteed Returns:</strong> The bot's past performance, including any win-rate statistics we may share, does not guarantee future results. Cryptocurrency markets are unpredictable and you may lose some or all of the capital in your trading account. AiPredict does not promise, guarantee, or imply any specific level of profit.</li>
                <li><strong>Your Account, Your Risk:</strong> All trades are executed on your personal exchange account. AiPredict does not hold, custody, or have access to your funds at any time. You are solely responsible for ensuring your exchange account is properly configured (including Isolated Margin mode, correct API permissions, and sufficient balance).</li>
                <li><strong>Technical Failures:</strong> The bot operates on automated systems that may experience downtime, delays, network failures, exchange API outages, or software bugs. Such events may result in missed trades, delayed stop-losses, incorrect position sizes, or other unintended outcomes. AiPredict is not liable for losses caused by technical failures on our systems, your exchange, or the broader internet infrastructure.</li>
                <li><strong>Exchange Responsibility:</strong> AiPredict is not responsible for the actions, policies, downtime, or failures of any third-party exchange (including but not limited to Bybit, Binance, or Hyperliquid). If your exchange freezes trading, halts withdrawals, changes their API, or experiences a security breach, AiPredict bears no liability for resulting losses.</li>
                <li><strong>Capital Caps & Tiers:</strong> Each subscription tier has a defined capital cap. The bot will not trade beyond your tier's cap regardless of your account balance. You agree not to circumvent tier limits through multiple accounts or other means.</li>
                <li><strong>Revocation:</strong> You may revoke the bot's access to your exchange account at any time by deleting or disabling your API key on the exchange. AiPredict may also revoke access at our discretion if we detect misuse, non-payment, or Terms violations.</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>5. API Key Handling</h2>
            <p>
                When you enroll in the automated trading service, you provide us with exchange API credentials (an API key and secret). You acknowledge the following:
            </p>
            <ul className="legal-list">
                <li>Your API keys are encrypted before storage and are never stored in plain text on our servers.</li>
                <li>API keys are only decrypted at the moment a trade is executed and are not cached, logged, or exposed to any human operator.</li>
                <li>You must configure your API key with <strong>trading permissions only</strong>. Specifically, you must not enable Withdrawal or Asset Transfer permissions. AiPredict is not liable for any loss resulting from API keys created with excessive permissions.</li>
                <li>You are responsible for the security of your exchange account. If your API key is compromised due to your own actions (sharing credentials, malware, phishing), AiPredict bears no responsibility.</li>
                <li>AiPredict reserves the right to delete your stored API keys at any time, including upon subscription cancellation, account termination, or at your request.</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>6. Payments, Subscriptions & Refunds</h2>
            <p>
                AiPredict offers multiple payment methods depending on the service:
            </p>
            <ul className="legal-list">
                <li><strong>Premium Subscriptions (Predictions & Analytics):</strong> Processed via Apple In-App Purchase, Google Play, or other integrated payment providers. Managed by the respective app store's billing policies.</li>
                <li><strong>Crypto AI Bot Subscriptions:</strong> Paid monthly in USDT (Tether) to a designated wallet address. Payment is due before activation and on a recurring monthly basis. Bot access is activated after payment confirmation and may take up to 24 hours.</li>
            </ul>
            <p>
                <strong>No Refunds:</strong> All payments — including subscription fees, USDT transfers, and in-app purchases — are final and non-refundable. For USDT payments, due to the irreversible nature of blockchain transactions, refunds are technically infeasible. By sending payment, you acknowledge this and waive any right to chargeback or reversal.
            </p>
            <p>
                <strong>Non-Payment & Suspension:</strong> If your monthly USDT payment is not received within 3 days of the due date, your bot access will be automatically suspended. Open positions will not be managed after suspension. AiPredict is not liable for losses incurred on unmanaged positions after account suspension.
            </p>
        </section>

        <section className="legal-section">
            <h2>7. Prohibited Activities</h2>
            <p>You agree not to engage in any of the following prohibited activities:</p>
            <ul className="legal-list">
                <li><strong>Reverse Engineering:</strong> Attempting to decompile, reverse engineer, or disassemble any portion of our AI models, trading algorithms, or source code.</li>
                <li><strong>Scraping:</strong> Using automated bots, spiders, or scrapers to access or extract data from the Platform.</li>
                <li><strong>Reselling:</strong> Selling, renting, or sub-licensing access to your account, our data, or our trading signals to third parties.</li>
                <li><strong>Signal Redistribution:</strong> Copying, forwarding, or publishing bot trade signals, entry/exit prices, or any proprietary trading data from the Platform to any external channel, group, or service.</li>
                <li><strong>Harmful Usage:</strong> Using the Service to generate content that is fraudulent, illegal, or violates the rights of others.</li>
                <li><strong>Multi-Account Abuse:</strong> Creating multiple accounts to circumvent tier capital caps, enrollment limits, or other restrictions.</li>
                <li><strong>Manual Interference:</strong> Manually opening or closing positions on the same assets the bot is trading, which may interfere with the bot's risk management (stop-losses, take-profit levels). You do so at your own risk.</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>8. Intellectual Property</h2>
            <p>
                All content, trademarks, logos, algorithms, trading strategies, and data on the Platform are the exclusive property of AiPredict. You are granted a limited, non-exclusive, non-transferable license to access the Service for personal use only. The trading signals generated by our bots are proprietary and may not be redistributed.
            </p>
        </section>

        <section className="legal-section">
            <h2>9. Indemnification</h2>
            <p>
                You agree to defend, indemnify, and hold harmless AiPredict, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable legal and accounting fees, arising out of or in any way connected with: (a) your access to or use of the Service; (b) your violation of these Terms; (c) any trading losses incurred through the automated trading service; (d) your failure to properly configure your exchange account or API permissions.
            </p>
        </section>

        <section className="legal-section">
            <h2>10. Limitation of Liability</h2>
            <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, AIPREDICT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, TRADING CAPITAL, OR DATA, WHETHER INCURRED DIRECTLY OR INDIRECTLY, RESULTING FROM: (A) YOUR USE OF THE AUTOMATED TRADING SERVICE; (B) TECHNICAL FAILURES, DELAYS, OR OUTAGES; (C) ACTIONS OR INACTIONS OF THIRD-PARTY EXCHANGES; (D) MARKET CONDITIONS INCLUDING EXTREME VOLATILITY, FLASH CRASHES, OR LIQUIDITY EVENTS; (E) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE.
            </p>
            <p>
                IN NO EVENT SHALL AIPREDICT'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE AMOUNT YOU PAID TO AIPREDICT IN THE THREE (3) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
            </p>
        </section>

        <section className="legal-section">
            <h2>11. Termination</h2>
            <p>
                AiPredict may terminate or suspend your account and access to the Service at our sole discretion, without prior notice, for any reason including but not limited to: violation of these Terms, non-payment, suspected fraud, or regulatory requirements.
            </p>
            <p>
                Upon termination of your bot subscription: (a) the bot will stop placing new trades on your behalf; (b) existing open positions will not be managed and will remain on your exchange account as-is; (c) you are solely responsible for managing any remaining positions; (d) your encrypted API keys will be deleted from our systems within 30 days.
            </p>
        </section>

        <section className="legal-section">
            <h2>12. Governing Law</h2>
            <p>
                These Terms shall be governed by and construed in accordance with the laws of the Republic of Ghana, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be resolved in the competent courts of Ghana.
            </p>
        </section>
    </div>
);

/* --- 2. RISK DISCLOSURE (EXPANDED — v2.0 Feb 2026) --- */
const RiskPolicy = () => (
    <div className="legal-text-fade">
        <div className="risk-banner">
            <Icon icon="solar:danger-triangle-bold" />
            <span><strong>CRITICAL WARNING:</strong> You can lose 100% of your capital. Read carefully.</span>
        </div>

        <p style={{ color: "#64748b", fontSize: "0.85rem", marginBottom: "1.5rem" }}>Last Updated: February 16, 2026</p>

        <section className="legal-section">
            <h2>1. No Financial Advice</h2>
            <p>
                AiPredict is a technology platform, not a financial advisor, investment manager, broker, or gambling operator. None of the information, predictions, signals, or automated trades provided by our AI, website, mobile app, or notifications constitutes a personal recommendation to buy, sell, or hold any asset, or to place any wager. You assume full and sole responsibility for any trading, investing, or betting decisions you make — whether manually or through our automated service.
            </p>
        </section>

        <section className="legal-section">
            <h2>2. Automated Trading Risks</h2>
            <p>If you use the Crypto AI Bot, you accept the following additional risks:</p>
            <ul className="legal-list">
                <li><strong>Loss of Capital:</strong> Automated trading can result in losses up to and including the entire balance of your trading account. The bot places leveraged trades, meaning losses can exceed your initial position size if margin protections fail.</li>
                <li><strong>Technical Failures:</strong> The bot relies on servers, APIs, internet connectivity, and exchange infrastructure — all of which can fail. Server crashes, network outages, exchange maintenance, or software bugs may cause missed stop-losses, delayed entries, duplicate orders, or failure to close positions.</li>
                <li><strong>Slippage & Liquidity:</strong> In fast-moving or thin markets, the bot may not execute at the intended price. Slippage can significantly impact your realized profit or loss, especially during high-volatility events.</li>
                <li><strong>Exchange Events:</strong> Exchanges may halt trading, delist assets, freeze accounts, change margin requirements, or experience security breaches without notice. The bot cannot protect you from exchange-level events.</li>
                <li><strong>Margin & Liquidation:</strong> Even with Isolated Margin mode, individual positions can be liquidated if the market moves sharply against you. While Isolated mode limits the damage to the margin allocated to that specific trade, the full margin for that position is still at risk.</li>
                <li><strong>Past Performance:</strong> Any performance statistics, win rates, or historical results we share are based on past data and specific market conditions. They are not a reliable indicator of future performance. Markets change, and strategies that worked before may stop working without warning.</li>
                <li><strong>Bot Errors:</strong> AI systems can and do make mistakes. The bot may misread a market signal, enter a trade at a bad time, set incorrect stop-loss levels, or fail to exit a position when it should. We continuously improve the system, but errors are an inherent risk of any software.</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>3. Cryptocurrency Risk Disclosure</h2>
            <p>Trading cryptocurrencies involves a significant risk of loss and is not suitable for every investor.</p>
            <ul className="legal-list">
                <li><strong>Extreme Volatility:</strong> Crypto assets can experience flash crashes where prices drop by 50% or more in minutes. Leveraged positions amplify these moves.</li>
                <li><strong>Liquidity Risk:</strong> You may not be able to sell your assets immediately if the market is illiquid or if an exchange halts trading.</li>
                <li><strong>Regulatory Risk:</strong> Changes in laws and regulations in any jurisdiction may materially affect the value of digital assets or the legality of trading them.</li>
                <li><strong>Technology Risk:</strong> Hacks, blockchain forks, smart contract failures, or wallet vulnerabilities can result in the irreversible loss of funds.</li>
                <li><strong>Stablecoin Risk:</strong> USDT and other stablecoins used for payments or trading may lose their peg, creating additional unexpected losses.</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>4. Sports Betting Risk Disclosure</h2>
            <p>
                Betting on sports events is speculative and involves a high degree of risk.
            </p>
            <ul className="legal-list">
                <li><strong>No Guarantees:</strong> "Favorites" lose. Referees make errors. Athletes get injured. Past performance is never a guarantee of future results.</li>
                <li><strong>Addiction Warning:</strong> Gambling can be addictive. If you or someone you know has a gambling problem, seek help immediately. Never bet money that is allocated for essential living expenses (rent, food, bills).</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>5. AI Limitations</h2>
            <p>
                Our prediction and trading models use probabilistic AI systems. You acknowledge the following inherent limitations:
            </p>
            <ul className="legal-list">
                <li><strong>AI Errors:</strong> The AI may occasionally generate confident-sounding but factually incorrect analysis, or enter trades based on misinterpreted signals.</li>
                <li><strong>Data Latency:</strong> Real-time data may be delayed by seconds or minutes due to network congestion, which can be critical in fast-moving markets.</li>
                <li><strong>Black Swan Events:</strong> AI models are trained on historical data and cannot predict unprecedented market events, regulatory actions, or acts of God.</li>
                <li><strong>Model Drift:</strong> Market conditions change over time. A strategy that has been profitable may become unprofitable as market dynamics shift.</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>6. Third-Party Reliability</h2>
            <p>
                AiPredict relies on third-party APIs for market data, exchange connectivity, and payment processing. We do not guarantee the accuracy, timeliness, or completeness of third-party data. We accept no liability for errors, outages, or losses caused by third-party providers including (but not limited to) Bybit, Binance, Hyperliquid, data feed providers, or blockchain networks.
            </p>
        </section>

        <section className="legal-section">
            <h2>7. Your Responsibility</h2>
            <p>
                By using any AiPredict service, you confirm that: (a) you understand the risks described above; (b) you are trading with capital you can afford to lose entirely; (c) you will not hold AiPredict responsible for any financial losses; (d) you have read and understood our Terms of Service in full.
            </p>
        </section>
    </div>
);

/* --- 3. PRIVACY POLICY (v2.0 Feb 2026) --- */
const PrivacyPolicy = () => (
    <div className="legal-text-fade">
        <p style={{ color: "#64748b", fontSize: "0.85rem", marginBottom: "1.5rem" }}>Last Updated: February 16, 2026</p>

        <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
                AiPredict ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our mobile application. By using the Service, you consent to the data practices described in this policy.
            </p>
        </section>

        <section className="legal-section">
            <h2>2. Information We Collect</h2>
            <p>We collect information in several ways to provide and improve our Service:</p>
            <ul className="legal-list">
                <li><strong>Personal Identification Information:</strong> When you register, we collect your name, email address, and authentication credentials (via Firebase).</li>
                <li><strong>Payment Information:</strong> For premium subscriptions, transactions are processed by third-party payment gateways. For Crypto AI Bot subscriptions, we record the USDT transaction hash and wallet address for payment verification. We do not store credit card details.</li>
                <li><strong>Exchange API Credentials:</strong> If you enroll in the automated trading service, we collect your exchange API key and API secret. These credentials are encrypted immediately upon submission and stored in encrypted form only. They are decrypted solely at the time of trade execution and are never logged, cached, or exposed in plain text.</li>
                <li><strong>Trading Data:</strong> We record trade activity executed by the bot on your account, including entry/exit prices, position sizes, profit/loss figures, and trade timestamps. This data is used to provide your dashboard, generate performance reports, and improve our trading systems.</li>
                <li><strong>Usage Data:</strong> We automatically collect data on how you interact with the Service, including pages visited, features used, time spent, and AI queries submitted.</li>
                <li><strong>Device & Technical Data:</strong> We collect your IP address, browser type, operating system, and device identifiers for security and rate-limiting purposes.</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected data for the following purposes:</p>
            <ul className="legal-list">
                <li><strong>Service Provision:</strong> To operate the Platform, authenticate your identity, execute trades on your behalf, and grant access to paid features.</li>
                <li><strong>Trade Execution:</strong> Your encrypted API credentials are used exclusively to place, modify, and close trades on your connected exchange account as part of the Crypto AI Bot service.</li>
                <li><strong>AI Processing:</strong> Your data inputs are sent to our AI systems to generate predictions and trading signals. These are anonymized where possible.</li>
                <li><strong>Communication:</strong> To send you administrative information, trade notifications, payment confirmations, security alerts, and support responses.</li>
                <li><strong>Security & Fraud Prevention:</strong> To monitor for suspicious activity, prevent unauthorized access, and enforce our Terms of Service.</li>
                <li><strong>Improvement:</strong> To analyze system performance, optimize our trading algorithms, and improve the user experience.</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>4. Sharing of Information</h2>
            <p>We do not sell, trade, or rent your personal identification information to others. We may share information with:</p>
            <ul className="legal-list">
                <li><strong>Service Providers:</strong> Third-party vendors who perform services on our behalf, such as hosting (Google Cloud/Firebase), payment processing, and email delivery.</li>
                <li><strong>Exchanges:</strong> Your API credentials are transmitted securely to your connected exchange (e.g., Bybit) solely for the purpose of executing trades. We do not share your credentials with any other party.</li>
                <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
                <li><strong>Business Transfers:</strong> If AiPredict is involved in a merger, acquisition, or asset sale, your personal data may be transferred. In such an event, encrypted API keys would either be transferred under the same security standards or deleted.</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>5. API Key Security</h2>
            <p>
                We take the security of your exchange API credentials extremely seriously:
            </p>
            <ul className="legal-list">
                <li>API keys are encrypted before storage and are never stored in plain text.</li>
                <li>Decryption occurs only at the moment of trade execution, in server memory, and keys are not written to logs or temporary files.</li>
                <li>Access to encryption systems is restricted and monitored.</li>
                <li>Upon account deletion or subscription cancellation, your API keys are permanently deleted from our systems within 30 days.</li>
                <li>We strongly recommend that users configure API keys with trading-only permissions and no withdrawal access. We are not responsible for losses resulting from misconfigured API permissions.</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>6. Cookies and Tracking Technologies</h2>
            <p>
                We use cookies, local storage, and similar tracking technologies to track the activity on our Service and hold certain information.
            </p>
            <p>
                <strong>Essential Cookies:</strong> Required for authentication (keeping you logged in).<br/>
                <strong>Analytics Cookies:</strong> Help us understand how you use the site.<br/>
                You can instruct your browser to refuse all cookies, but if you do not accept cookies, you may not be able to use some portions of our Service.
            </p>
        </section>

        <section className="legal-section">
            <h2>7. Data Retention</h2>
            <p>
                We retain your personal data only for as long as necessary. Specific retention periods:
            </p>
            <ul className="legal-list">
                <li><strong>Account Data:</strong> Retained while your account is active, deleted within 30 days of account deletion.</li>
                <li><strong>API Keys:</strong> Retained while your bot subscription is active, deleted within 30 days of cancellation or account deletion.</li>
                <li><strong>Trade History:</strong> Retained for up to 12 months after your subscription ends for performance reporting and dispute resolution.</li>
                <li><strong>Payment Records:</strong> Retained for up to 5 years as required for financial record-keeping and tax compliance.</li>
                <li><strong>Anonymized Analytics:</strong> May be retained indefinitely for system improvement purposes.</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>8. Data Security</h2>
            <p>
                The security of your data is important to us. We use industry-standard encryption for data in transit and at rest. Your account is secured by Firebase Authentication. Sensitive data such as API keys receive additional layers of encryption. However, no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee its absolute security.
            </p>
        </section>

        <section className="legal-section">
            <h2>9. International Data Transfers</h2>
            <p>
                Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your jurisdiction where data protection laws may differ. By using the Service, you consent to this transfer.
            </p>
        </section>

        <section className="legal-section">
            <h2>10. Your Data Rights</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul className="legal-list">
                <li><strong>Access:</strong> The right to request copies of your personal data.</li>
                <li><strong>Rectification:</strong> The right to request that we correct any information you believe is inaccurate.</li>
                <li><strong>Erasure:</strong> The right to request that we erase your personal data ("Right to be Forgotten"), subject to legal retention requirements.</li>
                <li><strong>API Key Deletion:</strong> You may request immediate deletion of your stored API keys at any time by contacting support or deleting your bot enrollment through the app.</li>
            </ul>
            <p>To exercise these rights, please contact us at support@aipredict.app.</p>
        </section>

        <section className="legal-section">
            <h2>11. Account Deletion</h2>
            <p>
                You can delete your account at any time through the app's Profile settings. When you delete your account:
            </p>
            <ul className="legal-list">
                <li>Your personal profile data will be permanently removed from our systems.</li>
                <li>Your authentication credentials will be deleted from Firebase.</li>
                <li>Any stored API keys will be permanently deleted.</li>
                <li>The bot will stop trading on your behalf immediately, but any open positions will remain on your exchange and become your responsibility.</li>
                <li>Any active subscriptions will not renew.</li>
            </ul>
        </section>

        <section className="legal-section">
            <h2>12. Children's Privacy</h2>
            <p>
                Our Service does not address anyone under the age of 18. We do not knowingly collect personally identifiable information from anyone under 18. If you are a parent or guardian and you are aware that your child has provided us with Personal Data, please contact us.
            </p>
        </section>

        <section className="legal-section">
            <h2>13. Changes to This Privacy Policy</h2>
            <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically.
            </p>
        </section>
    </div>
);

export default Legal;